import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get, post, del } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import { fmtBRL, competenceLabel, fmtPct } from '../lib/format';
import { Lock, Unlock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

export default function Fechamento() {
  const { competence, companies, closures, reloadClosures } = useApp();
  const [dre, setDre] = useState(null);

  useEffect(() => {
    (async () => {
      const k = await get('/dashboard/kpis', { competence });
      setDre(k);
    })();
  }, [competence, closures]);

  const lockCompetence = async (companyId = null) => {
    if (!window.confirm(`Confirma o fechamento de ${competenceLabel(competence)}? Após o fechamento, lançamentos desta competência não poderão ser alterados.`)) return;
    try {
      await post('/closures', { company_id: companyId, competence });
      toast.success('Competência fechada com sucesso');
      reloadClosures();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao fechar');
    }
  };

  const unlock = async (closureId) => {
    if (!window.confirm('Reabrir esta competência?')) return;
    await del(`/closures/${closureId}`);
    toast.success('Competência reaberta');
    reloadClosures();
  };

  const closuresForCompetence = closures.filter((c) => c.competence === competence);
  const consClosed = closuresForCompetence.find((c) => c.company_id === null);

  return (
    <div className="fade-in">
      <TopBar title="Fechamento Mensal" subtitle="Travamento de competências para preservar integridade gerencial" />
      <div className="px-8 py-7 space-y-5">

        {/* Fechamento Consolidado */}
        <SectionCard
          title={`Consolidado do Grupo - ${competenceLabel(competence)}`}
          subtitle="Fechar a competência consolidada trava todas as empresas simultaneamente"
          action={
            consClosed ? (
              <Button onClick={() => unlock(consClosed.id)} variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 gap-1.5" data-testid="btn-unlock-consolidated">
                <Unlock size={14} /> Reabrir
              </Button>
            ) : (
              <Button onClick={() => lockCompetence(null)} className="btn-gold gap-1.5" data-testid="btn-lock-consolidated">
                <Lock size={14} /> Fechar Competência
              </Button>
            )
          }
        >
          {dre && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="overline text-zinc-500">Receita</div>
                <div className="font-mono text-xl gold-text mt-1">{fmtBRL(dre.consolidated.receita)}</div>
              </div>
              <div>
                <div className="overline text-zinc-500">Despesas+Custos</div>
                <div className="font-mono text-xl red-text mt-1">{fmtBRL(dre.consolidated.custos + dre.consolidated.despesas)}</div>
              </div>
              <div>
                <div className="overline text-zinc-500">Lucro Líquido</div>
                <div className={`font-mono text-xl mt-1 ${dre.consolidated.lucro_liquido >= 0 ? 'emerald-text' : 'red-text'}`}>{fmtBRL(dre.consolidated.lucro_liquido)}</div>
              </div>
              <div>
                <div className="overline text-zinc-500">Margem</div>
                <div className="font-mono text-xl text-zinc-100 mt-1">{fmtPct(dre.consolidated.margem)}</div>
              </div>
            </div>
          )}
          {consClosed && (
            <div className="mt-4 flex items-center gap-2 text-xs text-emerald-400">
              <CheckCircle2 size={14} /> Fechado em {new Date(consClosed.locked_at).toLocaleString('pt-BR')}
            </div>
          )}
        </SectionCard>

        {/* Por Empresa */}
        <SectionCard title="Fechamento por Empresa" subtitle="Você pode fechar uma empresa antes do consolidado se necessário" padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full ceap-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th className="text-right">Receita</th>
                  <th className="text-right">Lucro Líquido</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((co) => {
                  const item = dre?.by_company.find((x) => x.company_id === co.id);
                  const closed = closuresForCompetence.find((c) => c.company_id === co.id);
                  return (
                    <tr key={co.id} data-testid={`closure-row-${co.id}`}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-7 rounded-sm" style={{ background: co.color }} />
                          <span className="text-zinc-100">{co.name}</span>
                        </div>
                      </td>
                      <td className="text-right font-mono gold-text">{fmtBRL(item?.receita || 0)}</td>
                      <td className={`text-right font-mono ${(item?.lucro_liquido || 0) >= 0 ? 'emerald-text' : 'red-text'}`}>
                        {fmtBRL(item?.lucro_liquido || 0)}
                      </td>
                      <td className="text-center">
                        {(closed || consClosed) ? (
                          <span className="pill pill-success"><Lock size={11} /> Fechado</span>
                        ) : (
                          <span className="pill pill-muted">Aberto</span>
                        )}
                      </td>
                      <td className="text-right">
                        {closed ? (
                          <Button size="sm" onClick={() => unlock(closed.id)} variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 text-xs h-7" data-testid={`btn-unlock-${co.id}`}>
                            <Unlock size={12} /> Reabrir
                          </Button>
                        ) : consClosed ? (
                          <span className="text-xs text-zinc-500">Travado pelo consolidado</span>
                        ) : (
                          <Button size="sm" onClick={() => lockCompetence(co.id)} className="btn-gold text-xs h-7" data-testid={`btn-lock-${co.id}`}>
                            <Lock size={12} /> Fechar
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* Histórico */}
        <SectionCard title="Histórico de Fechamentos" padded={false}>
          {closures.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">Nenhum fechamento realizado ainda</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ceap-table">
                <thead>
                  <tr>
                    <th>Competência</th>
                    <th>Escopo</th>
                    <th>Receita</th>
                    <th>Lucro</th>
                    <th>Fechado em</th>
                  </tr>
                </thead>
                <tbody>
                  {closures.map((c) => {
                    const co = companies.find((x) => x.id === c.company_id);
                    return (
                      <tr key={c.id}>
                        <td className="font-mono text-zinc-100">{competenceLabel(c.competence)}</td>
                        <td className="text-zinc-300">{c.company_id ? co?.name : <span className="gold-text">CONSOLIDADO</span>}</td>
                        <td className="font-mono text-zinc-200">{fmtBRL(c.snapshot?.receita || 0)}</td>
                        <td className={`font-mono ${(c.snapshot?.lucro_liquido || 0) >= 0 ? 'emerald-text' : 'red-text'}`}>
                          {fmtBRL(c.snapshot?.lucro_liquido || 0)}
                        </td>
                        <td className="font-mono text-zinc-400 text-xs">{new Date(c.locked_at).toLocaleString('pt-BR')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
